import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "lucide-react";
import PrintExportButtons from "@/components/PrintExportButtons";

const STATUS_COLORS = {
  pending: "bg-yellow-500",
  dispatched: "bg-orange-600",
  in_transit: "bg-blue-600",
  completed: "bg-emerald-600",
  cancelled: "bg-zinc-500",
};

export default function RequestsList() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const printRef = useRef(null);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    if (type !== "all") params.request_type = type;
    const { data } = await api.get("/requests", { params });
    setRows(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, type]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">Ledger</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-2">All requests</h1>
        </div>
        <PrintExportButtons
          targetRef={printRef}
          fileName={`requests-list-${new Date().toISOString().slice(0, 10)}`}
          testidPrefix="requests-export"
        />
      </div>

      <div ref={printRef} className="space-y-6">

      <div className="border-2 border-border bg-card p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-6 relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="requests-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by reference, company, PO#..."
            className="h-10 pl-9 rounded-none border-2"
          />
        </div>
        <div className="md:col-span-3">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="filter-type" className="rounded-none h-10 border-2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="pickup">Pickup</SelectItem>
              <SelectItem value="delivery">Delivery</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="filter-status" className="rounded-none h-10 border-2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="dispatched">Dispatched</SelectItem>
              <SelectItem value="in_transit">In Transit</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border-2 border-border bg-card">
        <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3 border-b-2 border-border text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
          <div className="col-span-2">Reference</div>
          <div className="col-span-2">From</div>
          <div className="col-span-2">To</div>
          <div className="col-span-2">Courier</div>
          <div className="col-span-2">Requester</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        <div className="divide-y-2 divide-border">
          {rows.length === 0 && (
            <div className="p-10 text-center text-sm text-muted-foreground">No requests match your filters.</div>
          )}
          {rows.map((r) => (
            <Link
              key={r.request_id}
              to={`/requests/${r.request_id}`}
              data-testid={`row-${r.request_id}`}
              className="flex flex-col sm:grid sm:grid-cols-12 gap-2 sm:gap-4 px-4 sm:px-6 py-4 hover:bg-accent transition-colors duration-200 sm:items-center"
            >
              <div className="sm:col-span-2 flex items-center justify-between sm:block">
                <div>
                  <span className="font-mono text-sm font-bold block">{r.reference_number}</span>
                  <span className="hidden sm:inline text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">
                    {r.request_type} &middot; {r.priority}
                  </span>
                </div>
                <span className="sm:hidden inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 border border-border">
                  <span className={`h-1.5 w-1.5 ${STATUS_COLORS[r.status] || "bg-zinc-400"}`} />
                  {r.status}
                </span>
              </div>
              <div className="sm:col-span-2 text-sm truncate"><span className="sm:hidden text-muted-foreground">From: </span>{r.pickup?.company || "—"}</div>
              <div className="sm:col-span-2 text-sm truncate"><span className="sm:hidden text-muted-foreground">To: </span>{r.delivery?.company || "—"}</div>
              <div className="sm:col-span-2 text-sm truncate" data-testid={`row-courier-${r.reference_number}`}>
                <span className="sm:hidden text-muted-foreground">Courier: </span>{r.courier?.company || "—"}
              </div>
              <div className="sm:col-span-2 text-sm truncate" data-testid={`row-requester-${r.reference_number}`}>
                <span className="sm:hidden text-muted-foreground">Requester: </span>{r.created_by_name || "—"}
              </div>
              <div className="hidden sm:block sm:col-span-2 text-right">
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 border border-border">
                  <span className={`h-1.5 w-1.5 ${STATUS_COLORS[r.status] || "bg-zinc-400"}`} />
                  {r.status}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}
